import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InfoTwoToneIcon from "@mui/icons-material/InfoTwoTone";

import DialogPopup from "../DialogPopup";
import LocalLoader from "../Loaders/Local";
import ProTipContentParser from "./ProTipContentParser";

import {
  useGetProtipManifestQuery,
  useGetProtipMutation,
} from "../../api/utility.api.slice";
import { showNotification } from "../../features/core/slices/notification.slice";
import "./ViewProTip.css";
import { getLatestApiSelector, marketName } from "../../utils/helpers";

const defaultProTipData = {
  title: " ",
  showTitle: true,
  showActionBtn: true,
  content: <LocalLoader progress />,
  actionButton: [
    { value: "notUseful", label: "NOT USEFUL" },
    { value: "useful", label: "USEFUL" },
  ],
  show: false,
};

function ProTip(props) {
  const { fieldKey, customClass = null, proTipProps = null } = props;
  const dispatch = useDispatch();
  const { applicationType, recordType } = useParams();
  const [proTipData, setProTipData] = useState(defaultProTipData);
  const formConfigMutation = useSelector((state) =>
    getLatestApiSelector(state, "getFormConfig")
  );

  const _processDefKey = applicationType || recordType || `${marketName}-misc`;
  let _taskDefinitionKey = "default";
  if (applicationType) _taskDefinitionKey = "start";
  if (applicationType && formConfigMutation?.data?.name === "record")
    _taskDefinitionKey = "record";
  if (recordType) _taskDefinitionKey = "record";

  const { data: protipManifest, isLoading: isProtipManifestLoading } =
    useGetProtipManifestQuery(
      { processDefKey: _processDefKey, taskDefinitionKey: _taskDefinitionKey },
      { skip: !_processDefKey || !_taskDefinitionKey }
    );
  const [getProtip, { error: proTipError }] = useGetProtipMutation();

  const openProTipDialog = async () => {
    if (proTipProps) {
      setProTipData({ ...proTipProps, show: true });
      return;
    }

    setProTipData({ ...defaultProTipData, show: true });

    let errorMsg = "";
    if (!protipManifest || !protipManifest.protips)
      errorMsg = "Protip manifest is missing";
    if (!_processDefKey) errorMsg = "Application type key is missing";
    if (protipManifest?.protips && !protipManifest.protips[fieldKey])
      errorMsg = "Protip not found";

    if (!_processDefKey) {
      /** @fallback to code protip templates */
      try {
        const content = await import(`@regional/templates/ProTip/${fieldKey}`);
        if (content && content.default) {
          setProTipData({
            ...defaultProTipData,
            ...content.default,
            show: true,
          });
          return;
        }
      } catch (error) {
        errorMsg = "Protip not found";
      }
    }

    if (errorMsg) {
      dispatch(showNotification({ type: "error", msg: errorMsg, show: true }));
      setProTipData(defaultProTipData);
      return;
    }

    const _protipData = await getProtip({
      market: marketName,
      processDefKey: _processDefKey,
      protipId: protipManifest.protips[fieldKey],
    }).unwrap();

    setProTipData({
      ...defaultProTipData,
      ..._protipData,
      content: (
        <Box className="ck ck-content">
          <ProTipContentParser rawContent={_protipData.content} />
        </Box>
      ),
      show: true,
    });
  };

  const closeProTipDialog = () => {
    setProTipData(defaultProTipData);
  };

  useEffect(() => {
    const error = proTipError;
    if (error) {
      const msg = "Protip is missing";
      dispatch(showNotification({ type: "error", msg, show: true }));
      setProTipData(defaultProTipData);
    }
  }, [dispatch, proTipError]);

  return (
    <>
      <IconButton
        className={customClass || "protipIcon"}
        aria-label="Pro Tip"
        onClick={(e) => {
          e.stopPropagation();
          openProTipDialog();
        }}
        onKeyDown={(e) => e.stopPropagation()}
        disable={isProtipManifestLoading}
      >
        <InfoTwoToneIcon fontSize="small" sx={{ cursor: "pointer" }} />
      </IconButton>
      <DialogPopup
        title={proTipData.title}
        showTitle={proTipData.showTitle}
        openPopup={proTipData.show}
        onCancel={closeProTipDialog}
        content={proTipData.content}
        buttonArray={proTipData?.actionButton?.map((btn) => ({
          id: btn.value,
          name: btn.label,
          variant: "outlined",
          onClickHandler: closeProTipDialog,
          show: true,
        }))}
        showActionBtn={proTipData.showActionBtn}
      />
    </>
  );
}
ProTip.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  customClass: PropTypes.string,
  proTipProps: PropTypes.shape({}),
};
ProTip.defaultProps = {
  customClass: null,
  proTipProps: null,
};
export default ProTip;
